// Helpers for the EmployeeConnect timesheet pages. Dates are YYYY-MM-DD strings
// throughout and are read from their parts, so no timezone can move a day.

const pad = (n) => String(n).padStart(2, '0');

const partsOf = (iso) => String(iso || '').split('-').map(Number);

export const isoFromDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const dateFromIso = (iso) => {
  const [y, m, d] = partsOf(iso);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const todayIso = () => isoFromDate(new Date());

export const addDays = (iso, days) => {
  const date = dateFromIso(iso);
  date.setDate(date.getDate() + days);
  return isoFromDate(date);
};

export const isValidIso = (iso) => /^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''));

// The semi-monthly pay period (1st-15th, 16th-end) containing a date.
export const periodFor = (iso) => {
  const [y, m, d] = partsOf(iso);
  if (d <= 15) return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-15` };
  const last = new Date(y, m, 0).getDate();
  return { start: `${y}-${pad(m)}-16`, end: `${y}-${pad(m)}-${pad(last)}` };
};

export const previousPeriod = (period) => periodFor(addDays(period.start, -1));
export const nextPeriod = (period) => periodFor(addDays(period.end, 1));

export const listDays = (start, end) => {
  const out = [];
  for (let cur = start; cur <= end; cur = addDays(cur, 1)) out.push(cur);
  return out;
};

export const isWeekend = (iso) => {
  const dow = dateFromIso(iso).getDay();
  return dow === 0 || dow === 6;
};

export const weekdayShort = (iso) => dateFromIso(iso).toLocaleDateString('en-US', { weekday: 'short' });
export const dayOfMonth = (iso) => partsOf(iso)[2];

export const formatDate = (iso, opts = { month: 'short', day: 'numeric' }) => (
  isValidIso(iso) ? dateFromIso(iso).toLocaleDateString('en-US', opts) : ''
);

export const formatLongDate = (iso) => formatDate(iso, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

export const formatPeriod = (period) => {
  if (!period) return '';
  const s = dateFromIso(period.start);
  const e = dateFromIso(period.end);
  return `${s.toLocaleDateString('en-US', { month: 'short' })} ${s.getDate()}–${e.getDate()}, ${e.getFullYear()}`;
};

export const formatTimestamp = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// "1.5", "1:30", "90m", "1h 30m", "2h" -> seconds. Null for anything else.
export const parseDurationInput = (raw) => {
  const text = String(raw ?? '').trim().toLowerCase();
  if (!text) return null;
  let match = text.match(/^(\d+):([0-5]?\d)$/);
  if (match) return Number(match[1]) * 3600 + Number(match[2]) * 60;
  match = text.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/);
  if (match && (match[1] || match[2])) {
    return Math.round((Number(match[1] || 0) * 3600) + (Number(match[2] || 0) * 60));
  }
  match = text.match(/^\d*\.?\d+$/);
  if (match) return Math.round(Number(text) * 3600);
  return null;
};

// Decimal hours, trimmed: 1.5, 2, 0.25.
export const formatHours = (hours) => {
  const n = Number(hours) || 0;
  return (Math.round(n * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
};

export const secondsToHours = (seconds) => Math.round(((Number(seconds) || 0) / 3600) * 100) / 100;

// "1:30" for the input box, so editing round-trips through parseDurationInput.
export const secondsToClock = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.round((total % 3600) / 60);
  return `${h}:${pad(m)}`;
};

// "1:02:07" for a running timer.
export const formatElapsed = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${pad(m)}:${pad(s)}`;
};

export const nowClock = (date = new Date()) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// A clock moved by some minutes, or null when that leaves the day.
export const shiftClock = (clock, minutes) => {
  if (!clock) return null;
  const [h, m] = String(clock).split(':').map(Number);
  const total = h * 60 + m + minutes;
  return total < 0 || total >= 24 * 60 ? null : `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
};

export const PERIOD_STATUS = {
  open: { label: 'Open', tone: 'muted', icon: 'bi-pencil' },
  submitted: { label: 'Submitted', tone: 'info', icon: 'bi-send' },
  approved: { label: 'Approved', tone: 'good', icon: 'bi-check-circle' },
  returned: { label: 'Returned', tone: 'warn', icon: 'bi-arrow-counterclockwise' },
};

export const ENTRY_SOURCE = {
  manual: null,
  timer: { icon: 'bi-stopwatch', label: 'Logged with the timer' },
  time_off: { icon: 'bi-lock', label: 'From an approved time-off request. Change the request to change this.' },
  timecamp_import: { icon: 'bi-cloud-download', label: 'Imported from TimeCamp' },
};

// Options for the task picker, grouped under their project. A project's own
// row is labelled as the project so it can be picked too (TimeCamp allows time
// on any node, and imported trees rely on it).
export const buildTaskOptions = (tasks, { includeIds = [] } = {}) => {
  const keep = new Set(includeIds.filter(Boolean));
  return (tasks || [])
    .filter((t) => (!t.effectiveArchived && t.systemRole !== 'paid_time_off') || keep.has(t.id))
    .map((t) => ({
      value: t.id,
      label: t.pathNames.length > 1 ? t.pathNames.slice(1).join(' / ') : `${t.name} (project)`,
      // Its own name and parent, for pickers that draw the tree itself.
      name: t.name,
      parentId: t.parentId || null,
      project: t.pathNames[0],
      rootId: t.rootId,
      path: t.path,
      billable: t.billable,
      rdEligible: Boolean(t.effectiveRdEligible),
      code: t.code,
    }));
};

// The validation failures (GET /timesheet/validation), sorted to where the
// page shows them:
//   byDate       a day's own problems (a missing meal break), by date
//   byEntry      one entry's problems (no description), by entry id
//   allByDate    every problem that has a day, entry ones included, by date:
//                the list under a day's entries - less those whose rule is set
//                to show only as the mark on the entry (display 'flag')
//   period       problems with no day of their own: a week or pay period
//                total (unit 'period'), like billable overtime
//   flagsByDate  allByDate rolled up to { severity, count } for the grid's
//                day headings
// Each problem is { key, ruleId, name, detail, severity: 'fail' | 'warn',
// date, entryIds, fix } - `fix` as GET /timesheet/validation describes it.
export const groupValidationIssues = (validations) => {
  const byDate = new Map();
  const byEntry = new Map();
  const allByDate = new Map();
  const flagsByDate = new Map();
  const period = [];
  let failCount = 0;
  let warnCount = 0;
  const add = (map, key, issue) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(issue);
  };
  (validations || []).forEach((rule) => {
    (rule.failures || []).forEach((failure, idx) => {
      const issue = {
        key: `${rule.id}:${failure.date || failure.period || ''}:${idx}`,
        ruleId: rule.id,
        name: rule.name,
        // A week or pay period's total says which one.
        detail: failure.period ? `${failure.period}: ${failure.detail}` : failure.detail,
        severity: failure.severity === 'warn' ? 'warn' : 'fail',
        date: failure.date || null,
        entryIds: failure.entryIds || [],
        fix: failure.fix || null,
      };
      if (issue.severity === 'fail') failCount += 1;
      else warnCount += 1;
      if (rule.unit === 'entry' && failure.entryIds?.length) {
        failure.entryIds.forEach((id) => add(byEntry, id, issue));
      } else if (issue.date) {
        add(byDate, issue.date, issue);
      } else {
        period.push(issue);
      }
      if (issue.date) {
        if (rule.display !== 'flag') add(allByDate, issue.date, issue);
        const flag = flagsByDate.get(issue.date) || { severity: 'warn', count: 0 };
        flagsByDate.set(issue.date, {
          severity: flag.severity === 'fail' || issue.severity === 'fail' ? 'fail' : 'warn',
          count: flag.count + 1,
        });
      }
    });
  });
  return { byDate, byEntry, allByDate, flagsByDate, period, failCount, warnCount };
};

// Tasks whose time is not work, by system role: they do not count toward the
// hours that make a meal break due (NON_WORK_TASK_IDS on the server).
const NON_WORK_ROLES = new Set(['meal_break', 'paid_break', 'paid_time_off', 'close_date']);

// Where a break that falls due after `dueHours` of work goes in a day: the end
// of the last timed work entry that keeps the day at or under that much work -
// or, when one entry runs through that point, the point itself. Null when the
// day has no start and end times to place it by.
export const breakClock = (dayEntries, taskById, dueHours) => {
  const due = Number(dueHours) * 3600;
  const work = dayEntries
    .filter((e) => e.startTime && e.endTime && !NON_WORK_ROLES.has(taskById.get(e.taskId)?.systemRole))
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
  let worked = 0;
  let boundary = null;
  for (const entry of work) {
    if (worked + entry.durationSeconds > due) {
      if (boundary) return boundary;
      const [h, m] = entry.startTime.split(':').map(Number);
      const at = h * 60 + m + Math.round((due - worked) / 60);
      return at < 24 * 60 ? `${pad(Math.floor(at / 60))}:${pad(at % 60)}` : entry.endTime;
    }
    worked += entry.durationSeconds;
    boundary = entry.endTime.slice(0, 5);
  }
  return boundary;
};

export const downloadCsv = (filename, rows) => {
  const escape = (value) => {
    const s = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((row) => row.map(escape).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// The Sunday-to-Saturday week containing a date (weekStart 0, as the app's
// date pickers use).
export const weekFor = (iso) => {
  const start = addDays(iso, -dateFromIso(iso).getDay());
  return { start, end: addDays(start, 6) };
};

// "1h 15m", "0h 00m".
export const formatHM = (seconds) => {
  const total = Math.max(0, Math.round((Number(seconds) || 0) / 60));
  return `${Math.floor(total / 60)}h ${pad(total % 60)}m`;
};

// '13:05' -> "01:05 pm".
export const formatClock12 = (clock) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(clock || ''));
  if (!m) return '';
  const h = Number(m[1]);
  return `${pad(h % 12 || 12)}:${m[2]} ${h < 12 ? 'am' : 'pm'}`;
};

// "Today, 16 October", "Yesterday, 15 October", "Thursday, 9 October".
export const formatDayHeading = (iso) => {
  const date = dateFromIso(iso);
  const rest = `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'long' })}`;
  const today = todayIso();
  if (iso === today) return `Today, ${rest}`;
  if (iso === addDays(today, -1)) return `Yesterday, ${rest}`;
  if (iso === addDays(today, 1)) return `Tomorrow, ${rest}`;
  return `${date.toLocaleDateString('en-US', { weekday: 'long' })}, ${rest}`;
};

// "Oct 11 – 17, 2026", or across months "Sep 28 – Oct 4, 2026".
export const formatRange = (start, end) => {
  const s = dateFromIso(start);
  const e = dateFromIso(end);
  const sm = s.toLocaleDateString('en-US', { month: 'short' });
  const em = e.toLocaleDateString('en-US', { month: 'short' });
  return sm === em
    ? `${sm} ${s.getDate()} – ${e.getDate()}, ${e.getFullYear()}`
    : `${sm} ${s.getDate()} – ${em} ${e.getDate()}, ${e.getFullYear()}`;
};
